import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const DEFAULT_IP = ['', '', '', ''];
const DEFAULT_VALIDITY = [false, false, false, false];

const IpAddressInput = ({
    initialValue, 
    setValue,
    setValid
}: {    
    initialValue: string;
    setValue: React.Dispatch<React.SetStateAction<string>>;
    setValid: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
 
    const [ipAddress, setIpAddress] = useState(DEFAULT_IP);
    const [validity, setValidity] = useState(DEFAULT_VALIDITY); // Track validity of each segment
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (initialized) return;
        if (initialValue && initialValue.split('.').length === 4) {
            const segments = initialValue.split('.');
            setIpAddress(segments);
            const validSegments = segments.map((item, i) => validateSegment(item, i));
            setValidity(validSegments);
            setValid(validSegments.every(item => item === true));
        }
        setInitialized(true);
    }, [initialized, initialValue]);

    const validateSegment = (segment: string, index: number) => {
        if (segment === '*' && (index === 2 || index === 3)) return true; // Allow '*' in the last two segments
        if (segment === '*') return false; // Prevent '*' in the first two segments
        const num = parseInt(segment, 10);
        return num >= 0 && num <= 255; // Check if segment is a valid number
    };
    
    const handleChange = (value, index) => {
        let newSegments = [...ipAddress];
        let newValidity = [...validity];
        const formattedValue = value.replace(/[^0-9*]/g, ''); // Allow only digits and '*'
    
        if (formattedValue === '*') {
            // Prevent * in the first and second segments
            if (index < 2) return;
    
            // Fill current and remaining segments with '*'
            newSegments = newSegments.map((_, i) => (i >= index ? '*' : newSegments[i]));
    
            // Validate all affected segments, including the ones automatically filled with '*'
            newSegments.forEach((segment, i) => {
                newValidity[i] = validateSegment(segment, i);
            });
        } else {
            // If entering digits, move to next segment when 3 digits are entered
            newSegments[index] = formattedValue.slice(0, 3); // Restrict to 3 digits max
            newValidity[index] = validateSegment(newSegments[index], index);

            // Automatically move to the next segment if 3 digits are entered
            if (formattedValue.length === 3 && index < 3 && newValidity[index] === true) {
                document.getElementById(`segment-${index + 1}`).focus();
            }
        }
    
        // Update state with the new segments and their validity
        setIpAddress(newSegments);
        setValidity(newValidity);
    
        const fullAddress = newSegments.join('.');

        // Validate full IP before updating parent state
        if (newSegments.every((segment, i) => (segment === '*' || validateSegment(segment, i)))) {
            setValue(fullAddress); // Update parent state if valid
            setValid(true);
        } else {
            setValid(false);
        }
    };

    return (
        <div style={{ display: 'flex', gap: '10px' }}>
            {ipAddress.map((segment, index) => (
                <CustomInput
                    key={index}
                    id={`segment-${index}`}
                    type="text"
                    value={segment}
                    onChange={(e) => handleChange(e.target.value, index)}
                    style={{ width: '75px', textAlign: 'center', fontSize: '16px' }}
                    maxLength={3} // Ensure max 3 characters
                    isValid={validity[index]} // Pass validation status to style the border
                />
            ))}
        </div>
    );
};

export default IpAddressInput;

// Styled input with dynamic border color
const CustomInput = styled.input<{ isValid: boolean }>`
    width: 75px;
    text-align: center;
    font-size: 16px;
    border: 2px solid;
    border-color: ${({ isValid }) => (isValid ? 'green' : 'red')}; // Green if valid, red if invalid
    outline: none;

    &:focus {
        border-color: ${({ isValid }) => (isValid ? 'darkgreen' : 'darkred')}; // Darker shade on focus
    }
`;
